/**
 * Base API client for REST endpoints
 */

import { ENDPOINTS } from "./endpoints.js";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL !== undefined
    ? import.meta.env.VITE_API_BASE_URL
    : "http://localhost:4000";

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

class ApiClient {
  constructor(baseURL = API_BASE_URL) {
    this.baseURL = baseURL;
    this.defaultHeaders = {
      "Content-Type": "application/json",
    };
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        ...this.defaultHeaders,
        ...options.headers,
      },
      credentials: "include", // Always include cookies for authentication
      ...options,
    };

    try {
      const response = await fetch(url, config);

      // Handle different response types
      const contentType = response.headers.get("content-type");
      let data;

      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        // Check for error in multiple formats (error, message, or default to status text)
        const errorMessage =
          data?.error ||
          data?.message ||
          `HTTP ${response.status}: ${response.statusText}`;
        throw new ApiError(errorMessage, response.status, data);
      }

      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      // Network or other errors
      throw new ApiError(error.message || "Network error occurred", 0, null);
    }
  }

  // GET request
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;

    return this.request(url, {
      method: "GET",
    });
  }

  // POST request
  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // PUT request
  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  // PATCH request
  async patch(endpoint, data = {}) {
    return this.request(endpoint, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  // DELETE request
  async delete(endpoint) {
    return this.request(endpoint, {
      method: "DELETE",
    });
  }

  // File upload
  async upload(endpoint, file, additionalData = {}) {
    const formData = new FormData();
    formData.append("file", file);

    // Add additional form fields
    Object.keys(additionalData).forEach((key) => {
      formData.append(key, additionalData[key]);
    });

    // Create request without Content-Type header to let browser set it
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      method: "POST",
      body: formData,
      credentials: "include", // Always include cookies for authentication
    };

    try {
      const response = await fetch(url, config);

      // Handle different response types
      const contentType = response.headers.get("content-type");
      let data;

      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        // Check for error in multiple formats (error, message, or default to status text)
        const errorMessage =
          data?.error ||
          data?.message ||
          `HTTP ${response.status}: ${response.statusText}`;
        throw new ApiError(errorMessage, response.status, data);
      }

      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      // Network or other errors
      throw new ApiError(error.message || "Network error occurred", 0, null);
    }
  }

  // Check if user is authenticated by making a request to /auth/me
  async checkAuth() {
    try {
      const response = await this.get(ENDPOINTS.AUTH.ME);
      return response.user || response;
    } catch (error) {
      console.error("checkAuth error:", error);
      return null;
    }
  }

  // Login with email/password
  async login(email, password) {
    return this.post(ENDPOINTS.AUTH.LOGIN, { email, password });
  }

  // Register new user
  async register(email, password, name) {
    return this.post(ENDPOINTS.AUTH.REGISTER, { email, password, name });
  }

  // Logout user
  async logout() {
    return this.post(ENDPOINTS.AUTH.LOGOUT);
  }
}

// Create singleton instance
const apiClient = new ApiClient();

export default apiClient;
export { ApiError };
